import {
    useCalculatedAgenda,
    useCalculatedAgendaAvailabilities
} from "crqlar-react-common/dist/utils/hooks/agenda/useAgendaData";
import {endOfWeek, startOfWeek} from "crqlar-react-common/dist/utils/date.util";
import {useReservationsState} from "../../../store/reservations.reducer";
import {
    useCurrentAppId
} from "crqlar-react-common/dist/utils/hooks/app/useOrganizationApps";
import {useCallback, useMemo} from "react";
import {Service} from "crqlar-react-common/dist/models/service/service.model";
import {
    Reservation
} from "crqlar-react-common/dist/models/reservation/reservation.model";
import {MbscCalendarEvent} from "@mobiscroll/react";

export const useWeeklyPlanner = () => {
    const appId = useCurrentAppId()

    const {
        fromDate,
        toDate,
        selectedAgenda
    } = useReservationsState(appId);

    const {
        data,
    } = useCalculatedAgenda(selectedAgenda?.id, startOfWeek(fromDate), endOfWeek(toDate));


    const agendaResourceIds = useMemo(() => {
        if (!data) return [];

        const resourceIds = data.items
            .flatMap(i => i.service.resourceIds)
            .filter(Boolean) as string[];

        return Array.from(new Set(resourceIds));
    }, [data]);

    const {
        data: agendaAvailabilities
    } = useCalculatedAgendaAvailabilities(agendaResourceIds, startOfWeek(fromDate), endOfWeek(toDate))


    const generateServiceEvents = useCallback((services: Service[], reservations: Reservation[]) => {
        let calendarEvents: MbscCalendarEvent[] = [];

        const fromDateStart = startOfWeek(fromDate);
        const toDateEnd = endOfWeek(toDate);

        services.forEach(service => {
            if (!service.availabilityBindings?.length) return;

            // Get availability IDs for this service
            const availabilityIds = service.availabilityBindings.flatMap(
                binding => binding.availabilityIds
            );

            // Filter agendaAvailabilities for this service's availabilities
            const serviceAgendaAvailabilities = agendaAvailabilities?.filter(aa =>
                aa.availabilityId && availabilityIds.includes(aa.availabilityId)
            );

            if (!serviceAgendaAvailabilities?.length) return;

            const serviceReservations = reservations.filter(
                res => res.serviceId === service.id
            );

            const firstColor = serviceAgendaAvailabilities.at(0)?.color;

            // Create events directly from agendaAvailabilities
            serviceAgendaAvailabilities.forEach(availabilityCalculation => {
                if (!availabilityCalculation.startTime || !availabilityCalculation.endTime) return;

                const start = new Date(availabilityCalculation.startTime);
                const end = new Date(availabilityCalculation.endTime);

                // Filter to date range
                if (start < fromDateStart || start > toDateEnd) return;

                // Find reservations for this occurrence
                const occurrenceReservations = serviceReservations.filter(res => {
                    if (!res.expectedStartDate) return false;
                    const resStartDate = new Date(res.expectedStartDate);
                    return resStartDate >= start && resStartDate < end;
                });

                const profileIds = occurrenceReservations.flatMap(
                    r => r.profileIds ?? []
                );

                const bookedCount = occurrenceReservations.reduce((sum, res) => {
                    const guestCount = res.guestCounts?.reduce(
                        (total, gc) => total + (gc.count || 0),
                        0
                    ) || 0;
                    return sum + guestCount;
                }, 0);

                const event: MbscCalendarEvent = {
                    start,
                    end,
                    title: availabilityCalculation.name,
                    resource: service.id,
                    id: service.id
                        ? `${service.id}-${availabilityCalculation.availabilityId}-${start.getTime()}`
                        : undefined,
                    color: availabilityCalculation.color ?? firstColor,
                    serviceId: service.id,
                    availabilityId: availabilityCalculation.availabilityId,
                    availability: availabilityCalculation,
                    reservations: occurrenceReservations,
                    profileIds: profileIds,
                    bookedCount: bookedCount,
                    type: availabilityCalculation.type
                };


                // Only add maxCapacity if capacityOverride exists
                if (availabilityCalculation.capacityOverride !== undefined && availabilityCalculation.capacityOverride !== null) {
                    event.maxCapacity = availabilityCalculation.capacityOverride;
                }

                calendarEvents.push(event);
            });
        });

        return calendarEvents;
    }, [agendaAvailabilities, fromDate, toDate]);


    return {
        generateServiceEvents
    }
}
